import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, Card, Alert, Badge, Spinner, Row, Col } from 'react-bootstrap';
import { modelApi } from '../services/api';

const ModelConfigTab = () => {
  const [config, setConfig] = useState({
    model_name: 'llama3:latest',
    embedding_model: 'mxbai-embed-large',
    temperature: 0.7,
    max_tokens: 1024,
    top_p: 0.9,
    top_k: 50,
    repetition_penalty: 1.15,
    system_prompt: '',
    context_prompt: ''
  });
  const [availableModels, setAvailableModels] = useState([]);
  const [availableEmbeddingModels, setAvailableEmbeddingModels] = useState([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [isLoadingEmbeddingModels, setIsLoadingEmbeddingModels] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentChatModel, setCurrentChatModel] = useState('llama3:latest');

  const loadAvailableModels = useCallback(async () => {
    try {
      setIsLoadingModels(true);
      const data = await modelApi.getAvailableModels();
      setAvailableModels(data.models || []);
    } catch (error) {
      console.error('Error loading available models:', error);
      setAvailableModels([]);
    } finally {
      setIsLoadingModels(false);
    }
  }, []);

  const loadEmbeddingModels = useCallback(async () => {
    try {
      setIsLoadingEmbeddingModels(true);
      const data = await modelApi.getAvailableEmbeddingModels();
      setAvailableEmbeddingModels(data.models || []);
    } catch (error) {
      console.error('Error loading embedding models:', error);
      setAvailableEmbeddingModels([]);
    } finally {
      setIsLoadingEmbeddingModels(false);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      setError(null);
      const data = await modelApi.getConfig();
      const nextConfig = data || getDefaultConfig();
      setConfig(nextConfig);
      setCurrentChatModel(nextConfig.model_name || 'llama3:latest');
    } catch (error) {
      // console.error('Error loading config:', error);
      // Extract error message from response
      let errorMessage = 'Failed to load model configuration';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      setConfig(getDefaultConfig());
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
    loadAvailableModels();
    loadEmbeddingModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getDefaultConfig = () => ({
    model_name: 'llama3:latest',
    embedding_model: 'mxbai-embed-large',
    temperature: 0.7,
    max_tokens: 1024,
    top_p: 0.9,
    top_k: 50,
    repetition_penalty: 1.15,
    system_prompt: "You are a helpful assistant specializing in policies and procedures. Use the provided context to answer questions accurately and cite relevant policy documents. If you cannot find the answer in the provided context, say so clearly.",
    context_prompt: "Based on the following policy documents:\n\n{context}\n\nQuestion: {question}\n\nAnswer:"
  });

  const handleConfigChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveConfig = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await modelApi.updateConfig(config);
      const savedConfig = result?.config || config;
      setConfig(savedConfig);
      setCurrentChatModel(savedConfig.model_name || config.model_name || 'llama3:latest');
      setSuccess('Model configuration updated successfully');
    } catch (error) {
      // console.error('Save error:', error);
      // Extract error message from response
      let errorMessage = 'Failed to save configuration';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetConfig = async () => {
    if (!window.confirm('Are you sure you want to reset the configuration to default values?')) {
      return;
    }

    setIsResetting(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await modelApi.resetConfig();
      const defaultConfig = result?.config || getDefaultConfig();
      setConfig(defaultConfig);
      setCurrentChatModel(defaultConfig.model_name || 'llama3:latest');
      setSuccess('Configuration reset to default values');
    } catch (error) {
      // console.error('Reset error:', error);
      // Extract error message from response
      let errorMessage = 'Failed to reset configuration';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsResetting(false);
    }
  };

  const getTemperatureDescription = () => {
    if (config.temperature <= 0.3) return "Very focused and deterministic responses";
    if (config.temperature <= 0.7) return "Balanced creativity and focus";
    if (config.temperature <= 1.0) return "More creative and varied responses";
    return "Highly creative but potentially inconsistent";
  };

  const getTopPDescription = () => {
    if (config.top_p <= 0.5) return "Very focused vocabulary";
    if (config.top_p <= 0.9) return "Balanced vocabulary selection";
    return "Full vocabulary range";
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">Loading model configuration...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h4 className="text-ut-blue mb-3">
          <i className="fas fa-cog me-2"></i>
          Model Configuration
        </h4>
        <p className="text-muted">
          Adjust the language model parameters to fine-tune response quality and behavior.
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          <i className="fas fa-check-circle me-2"></i>
          {success}
        </Alert>
      )}

      {/* Model Selection */}
      <Card className="mb-4">
        <Card.Header className="bg-ut-light">
          <div className="model-selection-header">
            <h5 className="mb-0">
              <i className="fas fa-robot me-2"></i>
              Model Selection
            </h5>
            <Badge className="current-model-bubble" pill>
              <i className="fas fa-comment-dots me-1"></i>
              Chat model: {currentChatModel}
            </Badge>
          </div>
        </Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>
              <strong>Language Model</strong>
              <small className="text-muted d-block">Select the language model to use for generating responses</small>
            </Form.Label>
            <div className="d-flex align-items-center gap-2">
              <Form.Select
                value={config.model_name || 'llama3'}
                onChange={(e) => handleConfigChange('model_name', e.target.value)}
                disabled={isLoadingModels}
              >
                {availableModels.length === 0 && !isLoadingModels && (
                  <option value={config.model_name || 'llama3:latest'}>{config.model_name || 'llama3:latest'}</option>
                )}
                {availableModels.map((model) => (
                  <option key={model.name} value={model.name}>
                    {model.name}
                  </option>
                ))}
              </Form.Select>
              <Button 
                variant="outline-primary" 
                size="sm" 
                onClick={loadAvailableModels}
                disabled={isLoadingModels}
              >
                {isLoadingModels ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-1" />
                    Loading...
                  </>
                ) : (
                  <>
                    <i className="fas fa-sync-alt me-1"></i>
                    Refresh
                  </>
                )}
              </Button>
            </div>
            {availableModels.length > 0 && (
              <Form.Text className="text-muted">
                <i className="fas fa-info-circle me-1"></i>
                {availableModels.length} model{availableModels.length !== 1 ? 's' : ''} available on Ollama server
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-0">
            <Form.Label>
              <strong>Embedding Model</strong>
              <small className="text-muted d-block">Model used to embed documents and queries for retrieval</small>
            </Form.Label>
            <div className="d-flex align-items-center gap-2">
              <Form.Select
                value={config.embedding_model || 'mxbai-embed-large'}
                onChange={(e) => handleConfigChange('embedding_model', e.target.value)}
                disabled={isLoadingEmbeddingModels}
              >
                {availableEmbeddingModels.length === 0 && !isLoadingEmbeddingModels && (
                  <option value={config.embedding_model || 'mxbai-embed-large'}>{config.embedding_model || 'mxbai-embed-large'}</option>
                )}
                {availableEmbeddingModels.map((model) => (
                  <option key={model.name} value={model.name}>
                    {model.name}
                  </option>
                ))}
              </Form.Select>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={loadEmbeddingModels}
                disabled={isLoadingEmbeddingModels}
              >
                {isLoadingEmbeddingModels ? (
                  <><Spinner as="span" animation="border" size="sm" className="me-1" />Loading...</>
                ) : (
                  <><i className="fas fa-sync-alt me-1"></i>Refresh</>
                )}
              </Button>
            </div>
            <Form.Text className="text-muted">
              <i className="fas fa-info-circle me-1"></i>
              {availableEmbeddingModels.length > 0
                ? `${availableEmbeddingModels.length} embedding model${availableEmbeddingModels.length !== 1 ? 's' : ''} available`
                : 'Changing the embedding model requires rebuilding the vector store'}
            </Form.Text>
          </Form.Group>
        </Card.Body>
      </Card>

      {/* Generation Parameters */}
      <Card className="mb-4">
        <Card.Header className="bg-ut-light">
          <h5 className="mb-0">
            <i className="fas fa-sliders-h me-2"></i>
            Generation Parameters
          </h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              {/* Temperature */}
              <div className="control-group mb-4">
                <Form.Label>
                  Temperature: {config.temperature}
                  <small className="text-muted d-block">{getTemperatureDescription()}</small>
                </Form.Label>
                <Form.Range
                  min="0"
                  max="2"
                  step="0.1"
                  value={config.temperature}
                  onChange={(e) => handleConfigChange('temperature', parseFloat(e.target.value))}
                />
                <div className="d-flex justify-content-between text-muted small">
                  <span>Focused (0)</span>
                  <span>Creative (2)</span>
                </div>
              </div>

              {/* Top P */}
              <div className="control-group mb-4">
                <Form.Label>
                  Top P: {config.top_p}
                  <small className="text-muted d-block">{getTopPDescription()}</small>
                </Form.Label>
                <Form.Range
                  min="0.1"
                  max="1"
                  step="0.1"
                  value={config.top_p}
                  onChange={(e) => handleConfigChange('top_p', parseFloat(e.target.value))}
                />
                <div className="d-flex justify-content-between text-muted small">
                  <span>Focused (0.1)</span>
                  <span>Diverse (1.0)</span>
                </div>
              </div>
            </Col>

            <Col md={6}>
              {/* Max Tokens */}
              <div className="control-group mb-4">
                <Form.Label>
                  Max Tokens: {config.max_tokens}
                  <small className="text-muted d-block">Maximum response length</small>
                </Form.Label>
                <Form.Range
                  min="256"
                  max="4096"
                  step="128"
                  value={config.max_tokens}
                  onChange={(e) => handleConfigChange('max_tokens', parseInt(e.target.value))}
                />
                <div className="d-flex justify-content-between text-muted small">
                  <span>Short (256)</span>
                  <span>Long (4096)</span>
                </div>
              </div>

              {/* Top K */}
              <div className="control-group mb-4">
                <Form.Label>
                  Top K: {config.top_k}
                  <small className="text-muted d-block">Number of token candidates</small>
                </Form.Label>
                <Form.Range
                  min="1"
                  max="100"
                  step="1"
                  value={config.top_k}
                  onChange={(e) => handleConfigChange('top_k', parseInt(e.target.value))}
                />
                <div className="d-flex justify-content-between text-muted small">
                  <span>Focused (1)</span>
                  <span>Diverse (100)</span>
                </div>
              </div>

              {/* Repetition Penalty */}
              <div className="control-group mb-4">
                <Form.Label>
                  Repetition Penalty: {(config.repetition_penalty || 1.15).toFixed(2)}
                  <small className="text-muted d-block">Penalises repeated phrases</small>
                </Form.Label>
                <Form.Range
                  min="1.0"
                  max="2.0"
                  step="0.05"
                  value={config.repetition_penalty || 1.15}
                  onChange={(e) => handleConfigChange('repetition_penalty', parseFloat(e.target.value))}
                />
                <div className="d-flex justify-content-between text-muted small">
                  <span>None (1.0)</span>
                  <span>Strong (2.0)</span>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Prompt Configuration */}
      <Card className="mb-4">
        <Card.Header className="bg-ut-light">
          <h5 className="mb-0">
            <i className="fas fa-comment-alt me-2"></i>
            Prompt Configuration
          </h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <div className="control-group">
                <Form.Label>
                  System Prompt
                  <small className="text-muted d-block">
                    Base instructions for the assistant's behavior
                  </small>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={8}
                  value={config.system_prompt}
                  onChange={(e) => handleConfigChange('system_prompt', e.target.value)}
                  placeholder="Enter system prompt..."
                />
              </div>
            </Col>

            <Col md={6}>
              <div className="control-group">
                <Form.Label>
                  Context Prompt Template
                  <small className="text-muted d-block">
                    Template for formatting context and questions (use {'{context}'} and {'{question}'})
                  </small>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={8}
                  value={config.context_prompt}
                  onChange={(e) => handleConfigChange('context_prompt', e.target.value)}
                  placeholder="Enter context prompt template..."
                />
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Action Buttons */}
      <div className="d-flex gap-2 justify-content-end">
        <Button
          variant="outline-secondary"
          onClick={handleResetConfig}
          disabled={isSaving || isResetting}
        >
          {isResetting ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Resetting...
            </>
          ) : (
            <>
              <i className="fas fa-undo me-2"></i>
              Reset to Default
            </>
          )}
        </Button>

        <Button
          variant="ut-primary"
          onClick={handleSaveConfig}
          disabled={isSaving || isResetting}
        >
          {isSaving ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Saving...
            </>
          ) : (
            <>
              <i className="fas fa-save me-2"></i>
              Save Configuration
            </>
          )}
        </Button>
      </div>

      {/* Configuration Tips */}
      <Card className="mt-4 border-0 bg-light">
        <Card.Body>
          <h6 className="text-ut-blue">
            <i className="fas fa-lightbulb me-2"></i>
            Configuration Tips
          </h6>
          <ul className="mb-0 text-muted">
            <li><strong>Temperature:</strong> Lower values (0.1-0.5) for more consistent, factual responses; higher values (0.7-1.2) for more creative responses</li>
            <li><strong>Top P:</strong> Controls vocabulary diversity; 0.9 is usually optimal for most use cases</li>
            <li><strong>Top K:</strong> Limits token candidates; 40-50 works well for focused responses</li>
            <li><strong>Max Tokens:</strong> Adjust based on desired response length; higher values allow longer responses but may impact performance</li>
          </ul>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ModelConfigTab;
